import { React } from '../../shared/react.js';
import { Icon } from '../../shared/icons.js';
import { useAppRuntime, useAppState } from '../../app/app.context.js';
import { effectiveSearchBudget, maximumSearchBudget, recall } from '../../app/app.selectors.js';
import { formatDuration, formatPercent } from '../../shared/utils/format.js';

const MINIMUM_BUDGET = 8;
const RANGE_STEPS = 1000;

function indexStatus(state: ReturnType<typeof useAppState>): string {
  if (state.indexPhase === 'hybrid') return 'Local HNSW';
  if (state.indexPhase === 'visualizing') return `Visual index ${state.visualIndexProgress.completed}/${state.visualIndexProgress.total}`;
  if (state.indexPhase === 'semantic-only') return (state.corpus?.source.mode === 'met-pack' || state.corpus?.source.mode === 'met-hybrid-pack') ? 'HNSW index' : 'Metadata index';
  return state.corpus ? 'Local HNSW' : 'Preparing metadata index';
}

function positionForBudget(budget: number, maximum: number): number {
  const clamped = Math.max(MINIMUM_BUDGET, Math.min(maximum, budget));
  const ratio = Math.log(clamped / MINIMUM_BUDGET) / Math.log(maximum / MINIMUM_BUDGET);
  return Math.round(ratio * RANGE_STEPS);
}

function budgetForPosition(position: number, maximum: number): number {
  const ratio = Math.max(0, Math.min(1, position / RANGE_STEPS));
  return Math.max(MINIMUM_BUDGET, Math.min(maximum, Math.round(MINIMUM_BUDGET * ((maximum / MINIMUM_BUDGET) ** ratio))));
}

export function AccuracyControl() {
  const runtime = useAppRuntime();
  const state = useAppState();
  const maximum = maximumSearchBudget(state);
  const budget = effectiveSearchBudget(state);
  const position = positionForBudget(budget, maximum);
  const progress = position / RANGE_STEPS * 100;
  const measuredRecall = recall(state);
  const approximate = state.approximate;

  return (
    <section className="accuracy-control" aria-labelledby="accuracy-control-title">
      <div className="accuracy-control__edge accuracy-control__edge--left">
        <Icon name="lightning" size={22}/>
        <div><strong>Faster</strong><span>Fewer graph visits</span></div>
      </div>

      <div className="accuracy-control__center">
        <div className="accuracy-control__title-row">
          <h2 id="accuracy-control-title">Search speed vs accuracy</h2>
          <span title="This logarithmic control directly changes the HNSW efSearch candidate budget."><Icon name="help" size={15}/></span>
        </div>
        <div className="range-shell" style={{ '--range-progress': `${progress}%` } as any}>
          <input
            type="range"
            min="0"
            max={RANGE_STEPS}
            step="1"
            value={position}
            onInput={(event: any) => runtime.setEfSearch(budgetForPosition(Number(event.currentTarget.value), maximum))}
            aria-label="Search speed versus accuracy"
            aria-valuetext={`HNSW search budget ${budget}`}
          />
        </div>
        <div className="accuracy-control__telemetry" aria-live="polite">
          <span className={`index-state index-state--${state.corpus && state.indexPhase === 'semantic' ? 'hybrid' : state.indexPhase}`}><i/>{indexStatus(state)}</span>
          <span><b>ef {budget}</b></span>
          <span>{approximate ? `${formatPercent(approximate.inspectedRatio, 2)} inspected` : 'Preparing index'}</span>
          <span>{approximate ? formatDuration(approximate.elapsedMs) : '—'}</span>
          {measuredRecall !== null && <span><b>{formatPercent(measuredRecall)} recall</b></span>}
        </div>
      </div>

      <div className="accuracy-control__edge accuracy-control__edge--right">
        <div><strong>More accurate</strong><span>Up to ef {maximum.toLocaleString()}</span></div>
        <Icon name="target" size={23}/>
      </div>
    </section>
  );
}
